import { PharmacistLayout } from "@/components/layout/pharmacist-layout";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, CheckCircle, Info, Users } from "lucide-react";

type TabId = "biosimilars" | "switching" | "counselling" | "nocebo";

interface Biosimilar {
  originator: string;
  originatorBrand: string;
  target: string;
  biosimilarName: string;
  manufacturer: string;
  approvalStatus: "EMA Approved" | "MHRA Approved" | "Approved";
  nhseStatus: "Commissioned" | "Preferred" | "Available" | "Part-commissioned";
  indications: string[];
}

const BIOSIMILARS: Biosimilar[] = [
  {
    originator: "Adalimumab",
    originatorBrand: "Humira",
    target: "TNF-α",
    biosimilarName: "Amgevita, Hyrimoz, Imraldi, Yuflyma, Idacio, Hadlima, Simlandi, Cyltezo",
    manufacturer: "Multiple (Amgen, Sandoz, Samsung Bioepis, Celltrion, Fresenius Kabi, Formycon)",
    approvalStatus: "EMA Approved",
    nhseStatus: "Commissioned",
    indications: ["RA", "PsA", "axSpA", "JIA", "IBD", "Uveitis", "Hidradenitis suppurativa"],
  },
  {
    originator: "Etanercept",
    originatorBrand: "Enbrel",
    target: "TNF-α",
    biosimilarName: "Benepali, Erelzi, Nepexto",
    manufacturer: "Samsung Bioepis, Sandoz, Polpharma Biologics",
    approvalStatus: "EMA Approved",
    nhseStatus: "Commissioned",
    indications: ["RA", "PsA", "axSpA", "JIA", "Plaque psoriasis"],
  },
  {
    originator: "Infliximab",
    originatorBrand: "Remicade",
    target: "TNF-α",
    biosimilarName: "Inflectra, Remsima, Zessly, Flixabi",
    manufacturer: "Celltrion, Pfizer, Sandoz, Samsung Bioepis",
    approvalStatus: "EMA Approved",
    nhseStatus: "Preferred",
    indications: ["RA", "PsA", "axSpA", "IBD", "Plaque psoriasis"],
  },
  {
    originator: "Rituximab",
    originatorBrand: "Mabthera",
    target: "CD20",
    biosimilarName: "Truxima, Rixathon, Ritemvia, Blitzima",
    manufacturer: "Celltrion, Sandoz, Mylan, Celltrion",
    approvalStatus: "EMA Approved",
    nhseStatus: "Commissioned",
    indications: ["RA", "ANCA vasculitis", "SLE", "Myositis (off-label)"],
  },
  {
    originator: "Abatacept",
    originatorBrand: "Orencia",
    target: "CTLA-4-Ig (CD80/CD86)",
    biosimilarName: "No biosimilars currently approved (as of 2025)",
    manufacturer: "—",
    approvalStatus: "EMA Approved",
    nhseStatus: "Commissioned",
    indications: ["RA"],
  },
  {
    originator: "Tocilizumab",
    originatorBrand: "Actemra / RoActemra",
    target: "IL-6 receptor",
    biosimilarName: "Tyenne, Tozycept (EMA approved 2023/2024)",
    manufacturer: "Fresenius Kabi, Zentiva",
    approvalStatus: "EMA Approved",
    nhseStatus: "Part-commissioned",
    indications: ["RA", "GCA", "sJIA", "COVID-19 (separate commissioning)"],
  },
  {
    originator: "Certolizumab pegol",
    originatorBrand: "Cimzia",
    target: "TNF-α",
    biosimilarName: "No biosimilars currently approved in UK (reference product still patented in some formulations)",
    manufacturer: "—",
    approvalStatus: "Approved",
    nhseStatus: "Commissioned",
    indications: ["RA", "PsA", "axSpA"],
  },
  {
    originator: "Golimumab",
    originatorBrand: "Simponi",
    target: "TNF-α",
    biosimilarName: "No biosimilars currently approved in UK (as of 2025)",
    manufacturer: "—",
    approvalStatus: "Approved",
    nhseStatus: "Commissioned",
    indications: ["RA", "PsA", "axSpA", "UC"],
  },
];

const statusColour: Record<string, string> = {
  "Commissioned": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Preferred": "bg-teal-50 text-teal-700 border-teal-200",
  "Available": "bg-blue-50 text-blue-700 border-blue-200",
  "Part-commissioned": "bg-amber-50 text-amber-700 border-amber-200",
};

function BiosimilarsTab() {
  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-600">
        UK-approved rheumatology biosimilars. NHSE commissioning status may vary by region and time — always verify with your local procurement/pharmacy team and check the <strong>NHSE Medicines Optimisation portal</strong> for the current preferred biosimilar list.
      </p>
      {BIOSIMILARS.map((b) => (
        <div key={b.originator} className="bg-white rounded-2xl border border-slate-200 p-5">
          <div className="flex items-start justify-between gap-4 flex-wrap mb-3">
            <div>
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <h3 className="font-bold text-slate-900">{b.originator}</h3>
                <span className="text-slate-400 text-sm">({b.originatorBrand})</span>
                <Badge variant="outline" className="text-xs">{b.target}</Badge>
              </div>
              <p className="text-xs text-slate-500">Indications: {b.indications.join(", ")}</p>
            </div>
            <Badge className={`border text-xs ${statusColour[b.nhseStatus] ?? ""}`}>
              NHSE: {b.nhseStatus}
            </Badge>
          </div>
          <div className="bg-slate-50 rounded-xl p-3">
            <p className="text-xs font-semibold text-slate-600 mb-1">UK-approved biosimilars:</p>
            <p className="text-sm text-slate-800">{b.biosimilarName}</p>
            {b.manufacturer !== "—" && <p className="text-xs text-slate-500 mt-1">Manufacturers: {b.manufacturer}</p>}
          </div>
        </div>
      ))}
    </div>
  );
}

function SwitchingPolicyTab() {
  return (
    <div className="space-y-6">
      <div className="bg-violet-50 border border-violet-200 rounded-xl p-5">
        <h3 className="font-bold text-violet-900 mb-2">NHSE Biosimilar Switching Policy</h3>
        <p className="text-sm text-violet-800 leading-relaxed">
          NHS England policy supports the use of biosimilars as cost-effective alternatives to originators. The NHSE Medicines Optimisation team recommends switching stable patients to biosimilars where a significant cost saving can be achieved, following appropriate patient communication and monitoring.
        </p>
      </div>

      <div className="space-y-4">
        <h3 className="font-semibold text-slate-900">Pharmacist Responsibilities in Switching</h3>
        {[
          {
            step: "1. Review eligibility",
            detail: "Confirm patient is clinically stable (no recent flare, disease activity within target). Biosimilar switching in acute flare or active disease adjustment is generally not recommended.",
          },
          {
            step: "2. Verify biosimilar interchangeability",
            detail: "MHRA has approved biosimilars as interchangeable with their reference products for authorised indications. Automatic substitution at dispensing level is not standard practice in UK hospital pharmacy without prescriber involvement. Non-medical prescribers should ensure the prescription reflects the specific biosimilar being dispensed.",
          },
          {
            step: "3. Patient communication",
            detail: "Explain the switch using the BSR patient information principles: the biosimilar has the same clinical effect; why the switch is occurring (cost-effectiveness); any differences in device/formulation; what to do if they experience changes after switching.",
          },
          {
            step: "4. Document the switch",
            detail: "Record in the patient record: date of switch, originator lot number, biosimilar product name and batch number. Pharmacovigilance reporting requirement — any adverse events post-switch should be reported via the Yellow Card scheme.",
          },
          {
            step: "5. Post-switch monitoring",
            detail: "Schedule a follow-up call or clinic appointment 4–6 weeks post-switch. Assess disease activity (DAS28/BASDAI/PROs), injection site reactions, and patient-reported concerns. Document clinical outcomes.",
          },
          {
            step: "6. Reporting",
            detail: "Any adverse events or unexpected loss of efficacy following biosimilar switching must be reported via the MHRA Yellow Card scheme, specifying brand name and batch number.",
          },
        ].map((item) => (
          <div key={item.step} className="bg-white rounded-xl border border-slate-200 p-4">
            <p className="text-sm font-bold text-slate-900 mb-1">{item.step}</p>
            <p className="text-sm text-slate-700">{item.detail}</p>
          </div>
        ))}
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
        <p className="text-sm font-bold text-amber-800 mb-1 flex items-center gap-2">
          <AlertTriangle className="h-4 w-4" /> Multiple Switching ("cross-switching")
        </p>
        <p className="text-sm text-amber-900">
          Evidence on switching between multiple biosimilars (rather than originator to biosimilar) is limited. BSR advises caution with multiple sequential switches — where possible, maintain the patient on a single biosimilar once switched. Always document brand name and batch number.
        </p>
      </div>
    </div>
  );
}

function CounsellingTab() {
  const counsellingPoints = [
    {
      question: "What is a biosimilar?",
      answer: "A biosimilar is a biological medicine that is highly similar to an already-approved biological medicine (the reference product/originator). It has been shown to be as safe and effective as the originator in clinical studies approved by the MHRA or EMA.",
    },
    {
      question: "Is it as safe and effective?",
      answer: "Yes. Biosimilars undergo rigorous testing to show they work in the same way and have the same safety profile as the originator. They are not generic medicines — they are complex biological molecules made by living cells, but the approval process confirms clinical equivalence.",
    },
    {
      question: "Why are we switching?",
      answer: "The NHS is switching patients to biosimilars because they cost significantly less than the original product, while providing the same clinical benefit. This saves NHS resources that can be reinvested into patient care.",
    },
    {
      question: "Will I notice a difference?",
      answer: "Most patients do not notice any difference when switching to a biosimilar. Occasionally, injection site reactions or minor differences in the device (pen or syringe) may be noticed — please report any concerns. Some patients report a nocebo effect (feeling worse due to expectation) — we will discuss this with you.",
    },
    {
      question: "Is the device the same?",
      answer: "The injection device may differ between the originator and biosimilar (e.g. different pen design, needle length, or injection speed). Your pharmacist or nurse will train you on the new device before your first injection.",
    },
    {
      question: "What should I do if something changes?",
      answer: "If your condition flares, you have new side effects, or you are concerned about anything after switching, contact your rheumatology nurse or team. Do not stop the medication without speaking to us first. Report any suspected side effects to your team and to the MHRA Yellow Card scheme.",
    },
    {
      question: "Can I switch back?",
      answer: "In most cases, yes. If there is a clinical reason to switch back (e.g. loss of efficacy or adverse reaction), discuss this with your rheumatologist. Spontaneous switching without clinical rationale is not recommended.",
    },
  ];

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-600 leading-relaxed">
        Structured counselling framework for patients being switched to a biosimilar. Use this as a guide for the pharmacist counselling consultation — tailor to individual patient needs and health literacy.
      </p>
      <div className="space-y-3">
        {counsellingPoints.map((point) => (
          <div key={point.question} className="bg-white rounded-xl border border-slate-200 p-4">
            <p className="text-sm font-bold text-slate-900 mb-2 flex items-center gap-2">
              <Users className="h-4 w-4 text-violet-500 shrink-0" />
              {point.question}
            </p>
            <p className="text-sm text-slate-700 leading-relaxed">{point.answer}</p>
          </div>
        ))}
      </div>
      <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
        <p className="text-sm font-bold text-emerald-800 mb-2 flex items-center gap-2">
          <CheckCircle className="h-4 w-4" /> Counselling Checklist
        </p>
        <ul className="space-y-1.5">
          {[
            "Patient has read or been given the biosimilar patient information leaflet",
            "Biosimilar explained as equally safe and effective",
            "Reason for switch clearly explained",
            "Device training completed (if injectable)",
            "Patient knows what to do if they notice a change",
            "Yellow Card reporting explained",
            "Switch documented in patient record with product name, batch number, and date",
            "Follow-up appointment/call scheduled (4–6 weeks)",
          ].map((item) => (
            <li key={item} className="flex items-start gap-2 text-sm text-emerald-900">
              <CheckCircle className="h-3.5 w-3.5 text-emerald-500 shrink-0 mt-0.5" />
              {item}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function NoceboTab() {
  return (
    <div className="space-y-6">
      <div className="bg-violet-50 border border-violet-200 rounded-xl p-5">
        <h3 className="font-bold text-violet-900 mb-2 flex items-center gap-2">
          <Info className="h-5 w-5" /> What is the Nocebo Effect?
        </h3>
        <p className="text-sm text-violet-800 leading-relaxed">
          The nocebo effect is the opposite of the placebo effect — a patient experiences negative outcomes or side effects due to negative expectations or anxiety about a new treatment, rather than the pharmacological action of the drug. In biosimilar switching, nocebo effects account for a significant proportion of patient-reported adverse events, with studies suggesting 10–30% of patients may report worsening symptoms without pharmacological cause.
        </p>
      </div>

      <div>
        <h3 className="font-semibold text-slate-900 mb-3">Evidence Base</h3>
        <div className="space-y-3">
          {[
            {
              study: "NOR-SWITCH (Norwegian multicentre RCT, 2017)",
              finding: "Switching stable patients from originator infliximab (Remicade) to biosimilar infliximab (CT-P13/Inflectra) was non-inferior in disease control across all conditions. Most treatment failures were attributed to nocebo effect, not pharmacological failure.",
            },
            {
              study: "DANBIO registry data",
              finding: "Analysis showed higher discontinuation rates in non-blinded switching — patients who knew they were being switched to a biosimilar had higher rates of perceived worsening compared to blinded groups.",
            },
            {
              study: "BSR Biosimilar Position Statement",
              finding: "BSR endorses biosimilar switching with appropriate patient communication as key to minimising nocebo effects. Open, transparent, and empathetic communication is the most effective intervention.",
            },
          ].map((item) => (
            <div key={item.study} className="bg-white rounded-xl border border-slate-200 p-4">
              <p className="text-sm font-bold text-slate-900 mb-1">{item.study}</p>
              <p className="text-sm text-slate-700">{item.finding}</p>
            </div>
          ))}
        </div>
      </div>

      <div>
        <h3 className="font-semibold text-slate-900 mb-3">Pharmacist Strategies to Minimise Nocebo Effect</h3>
        <div className="space-y-3">
          {[
            {
              strategy: "Proactive, transparent communication",
              detail: "Explain the switch before it happens — surprise switches are associated with higher nocebo rates. Use positive, factual framing: 'This medicine works in exactly the same way' rather than 'it's just as good'.",
            },
            {
              strategy: "Address anxieties explicitly",
              detail: "Ask patients directly: 'Do you have any concerns about switching?' Acknowledge that some patients worry, but explain the evidence shows most people don't notice a difference.",
            },
            {
              strategy: "Contextualise device differences",
              detail: "Preemptively inform about any differences in the injection pen/syringe. Train on the device. Do not allow first use of a new device without education — uncertainty about technique can be misattributed to the drug.",
            },
            {
              strategy: "Avoid negative language",
              detail: "Pharmacists and nurses should use consistent, confident language. Avoid phrases like 'if you have problems' — instead: 'We'll check in with you in 4–6 weeks to confirm everything is going smoothly'.",
            },
            {
              strategy: "Set up a monitoring touchpoint",
              detail: "A scheduled follow-up call 4–6 weeks post-switch normalises the experience, provides reassurance, and gives patients an opportunity to raise concerns within a clinical context rather than assuming they are failing on the new treatment.",
            },
            {
              strategy: "Provide written information",
              detail: "Give patients the BSR/NHSE biosimilar patient information leaflet. Having written material to refer to at home reduces anxiety and nocebo susceptibility.",
            },
          ].map((item) => (
            <div key={item.strategy} className="bg-white rounded-xl border border-slate-200 p-4">
              <p className="text-sm font-bold text-slate-900 mb-1">{item.strategy}</p>
              <p className="text-sm text-slate-700">{item.detail}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const TABS: { id: TabId; label: string }[] = [
  { id: "biosimilars", label: "UK Biosimilars" },
  { id: "switching", label: "Switching Policy" },
  { id: "counselling", label: "Counselling Points" },
  { id: "nocebo", label: "Nocebo Management" },
];

export default function PharmacistBiosimilars() {
  const [activeTab, setActiveTab] = useState<TabId>("biosimilars");

  return (
    <PharmacistLayout title="Biosimilar Guide">
      <div className="space-y-6">
        <div className="bg-violet-50 border border-violet-200 rounded-xl px-4 py-3 text-sm text-violet-800">
          <strong>UK Rheumatology Biosimilar Reference.</strong> Based on NHSE Medicines Optimisation guidance, BSR position statements, MHRA approvals, and published switching evidence. Verify current NHSE preferred biosimilar list with your procurement team.
        </div>

        <div className="border-b border-slate-200">
          <div className="flex gap-1">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-3 text-sm font-medium transition-colors border-b-2 -mb-px ${
                  activeTab === tab.id
                    ? "border-violet-600 text-violet-700"
                    : "border-transparent text-slate-600 hover:text-slate-900"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          {activeTab === "biosimilars" && <BiosimilarsTab />}
          {activeTab === "switching" && <SwitchingPolicyTab />}
          {activeTab === "counselling" && <CounsellingTab />}
          {activeTab === "nocebo" && <NoceboTab />}
        </div>
      </div>
    </PharmacistLayout>
  );
}
